import React from "react"
import { ScrollView, StyleSheet, View } from "react-native"
import TextM from "@common/components/text/TextM"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import MarkdownWithLatex from "../../../tutor/home/MarkdownWithLatex"
import { UNKNOWN_NOTION_LABEL, typeConfig } from "./correctionPointTypes"

/**
 * One structured-correction point card — marker number, notion, "À vérifier"
 * pill and the markdown+LaTeX explication. Shared by the vertical analysis
 * list (StructuredCorrectionBlock) and the horizontal bottom-panel carousel
 * (CorrectionPointsPanel); stays surface-agnostic.
 *
 * Explication sizing modes:
 *  - "natural" (default): web renders to natural height; native auto-sizes
 *    the WebView to its content (never truncates) — for vertical lists.
 *  - "fill": the explication flex-fills the card and scrolls internally —
 *    for fixed-height carousel cards (native WebView scrolls by itself).
 */

const Explication = ({ value, mode }) => {
    if (!value) return null

    if (mode === "fill") {
        return IS_WEB ? (
            <ScrollView style={{ flex: 1 }}>
                <MarkdownWithLatex value={value} />
            </ScrollView>
        ) : (
            <MarkdownWithLatex value={value} style={{ flex: 1 }} />
        )
    }

    return IS_WEB ? (
        <MarkdownWithLatex value={value} />
    ) : (
        <MarkdownWithLatex value={value} autoHeight />
    )
}

const CorrectionPointCard = ({
    point,
    dimmed = false,
    selected = false,
    collapsible = false,
    expanded = true,
    explicationMode = "natural",
    // Lets a carousel keep off-screen cards light (the native explication is
    // a WebView) — the card shell still renders, only the explication skips.
    mountExplication = true,
    onPress,
    onLayout,
    style,
}) => {
    const cfg = typeConfig(point?.type)
    const notion = point?.inconnue
        ? UNKNOWN_NOTION_LABEL
        : point?.skill_part_name || point?.notion || UNKNOWN_NOTION_LABEL

    const header = (
        <View style={styles.cardHeader}>
            <View style={[styles.marker, { backgroundColor: cfg.color }]}>
                <TextM fontWeight="Bold" fontSize={13} color={theme.colors.white}>
                    {point?.index}
                </TextM>
            </View>

            <TextM fontWeight="Bold" fontSize={15} wrap style={styles.notion}>
                {notion}
            </TextM>

            {point?.certain === false ? (
                <View style={styles.uncertainPill}>
                    <TextM fontSize={10} fontWeight="Bold" color={theme.colors.grey}>
                        À vérifier
                    </TextM>
                </View>
            ) : null}

            {collapsible ? (
                <FontAwesome5
                    name={expanded ? "chevron-up" : "chevron-down"}
                    size={12}
                    color={theme.colors.light}
                />
            ) : null}
        </View>
    )

    return (
        <View
            onLayout={onLayout}
            style={[
                styles.card,
                { borderLeftColor: cfg.color },
                explicationMode === "fill" && { flex: 1 },
                selected && {
                    backgroundColor: cfg.light,
                    borderColor: cfg.color,
                },
                dimmed && styles.cardDimmed,
                style,
            ]}
        >
            {onPress ? (
                <TouchableRippleM onPress={onPress}>{header}</TouchableRippleM>
            ) : (
                header
            )}
            {(!collapsible || expanded) && mountExplication ? (
                <View
                    style={[
                        styles.explicationWrap,
                        explicationMode === "fill" && { flex: 1 },
                    ]}
                >
                    <Explication
                        value={point?.explication}
                        mode={explicationMode}
                    />
                </View>
            ) : null}
        </View>
    )
}

const styles = StyleSheet.create({
    card: {
        backgroundColor: theme.colors.white,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        borderLeftWidth: 4,
        overflow: "hidden",
    },
    cardDimmed: {
        opacity: 0.35,
    },
    cardHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
        paddingVertical: 10,
        paddingHorizontal: 14,
    },
    marker: {
        width: 24,
        height: 24,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
    },
    notion: {
        flex: 1,
    },
    uncertainPill: {
        paddingVertical: 3,
        paddingHorizontal: 9,
        borderRadius: 10,
        backgroundColor: theme.colors.lightMore,
    },
    explicationWrap: {
        paddingHorizontal: 14,
        paddingBottom: 10,
    },
})

export default CorrectionPointCard
